import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function EmbedForeignPage(): Node {
  return html`
    <div>
      <h1>embedForeign</h1>
      <p><code>embedForeign</code> mounts React, Vue, Svelte, or any framework component inside an onefold application. You control the mount/unmount lifecycle.</p>

      <h2>React Integration</h2>
      ${CodeBlock(`import { embedForeign } from 'onefold/interop';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { DatePicker } from './react-components/DatePicker';

function App(): Node {
  const datePicker = embedForeign({
    mount: (container, props) => {
      const root = createRoot(container);
      root.render(React.createElement(DatePicker, props));
      return { root };
    },
    unmount: (container, { root }) => {
      root.unmount();
    },
    props: {
      onChange: (date: Date) => console.log('Selected:', date),
      minDate: new Date(),
    },
  });

  return html\`
    <div>
      <h2>Pick a date</h2>
      \${datePicker}
    </div>
  \`;
}`)}

      <h2>Vue Integration</h2>
      ${CodeBlock(`import { embedForeign } from 'onefold/interop';
import { createApp } from 'vue';
import ChartComponent from './vue-components/Chart.vue';

const vueChart = embedForeign({
  mount: (container, props) => {
    const app = createApp(ChartComponent, props);
    app.mount(container);
    return { app };
  },
  unmount: (container, { app }) => {
    app.unmount();
  },
  props: { data: [1, 2, 3, 4, 5] },
});`)}

      ${Callout('embedForeign creates a container div and passes it to your mount function. You own the lifecycle — mount however the foreign framework requires.')}

      <h2>Svelte Integration</h2>
      ${CodeBlock(`import { embedForeign } from 'onefold/interop';
import Counter from './svelte-components/Counter.svelte';

const svelteCounter = embedForeign({
  mount: (container, props) => {
    const component = new Counter({ target: container, props });
    return { component };
  },
  unmount: (container, { component }) => {
    component.\$destroy();
  },
  props: { initial: 0 },
});`)}

      <h2>API</h2>
      <table>
        <tr><th>Option</th><th>Type</th><th>Description</th></tr>
        <tr><td><code>mount</code></td><td>(container: HTMLElement, props: P) => C</td><td>Mount the foreign component. Return a context for cleanup.</td></tr>
        <tr><td><code>unmount</code></td><td>(container: HTMLElement, context: C) => void</td><td>Cleanup when the node leaves the DOM.</td></tr>
        <tr><td><code>props</code></td><td>P</td><td>Props passed to the mount function.</td></tr>
      </table>

      <h2>Try It</h2>
      <p>Simulates embedding a "foreign" widget (like a React/Vue component) using <code>embedForeign</code>:</p>

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { embedForeign } from 'onefold/interop';

function App() {
  const count = createSignal(0);

  // Simulate a "foreign framework" widget rendered imperatively
  // In a real app this would be: ReactDOM.createRoot(el).render(<Counter />)
  const foreignWidget = embedForeign({
    tag: 'div',
    render: (el) => {
      el.style.cssText = 'padding:16px;border:2px dashed #818cf8;border-radius:8px;background:#eef2ff';
      el.innerHTML = '<p style="font-size:13px;color:#4338ca;font-weight:600;margin-bottom:8px">[Foreign Widget]</p>' +
        '<p style="font-size:13px">This simulates a React/Vue component mounted with embedForeign.</p>' +
        '<p style="font-size:12px;color:#666;margin-top:8px">It has its own internal state and rendering — onefold only owns the container element.</p>' +
        '<button id="foreign-btn" style="margin-top:8px">Foreign Click: 0</button>';
      let clicks = 0;
      el.querySelector('#foreign-btn').onclick = () => {
        clicks++;
        el.querySelector('#foreign-btn').textContent = 'Foreign Click: ' + clicks;
      };
      return { el, cleanup: () => { el.innerHTML = ''; } };
    },
    unrender: (ctx) => {
      ctx.cleanup();
      console.log('Foreign widget unmounted');
    },
  });

  const showForeign = createSignal(true);

  return html\`
    <div>
      <h3>embedForeign — Third-Party Widget</h3>
      <p style="font-size:13px;color:#666;margin-bottom:12px">
        embedForeign provides a container element. The foreign framework renders into it. onefold handles cleanup when the node is removed.
      </p>
      <button onclick=\${() => showForeign.set(v => !v)} style="margin-bottom:12px">
        \${() => showForeign() ? 'Unmount Foreign Widget' : 'Mount Foreign Widget'}
      </button>
      \${() => showForeign() ? foreignWidget : html\`<p style="color:#94a3b8;padding:16px;text-align:center">Widget unmounted. unrender() was called for cleanup.</p>\`}
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'embedForeign — Mount/Unmount Third-Party Widget')}
    </div>
  `;
}
